import { CallIntelligenceService } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { MockAPIRouter } from '@frontend/fetch';
import { serviceName } from '../../service';
import * as CallIntelTypes from '../../types';
import { extractCallFilterFromRequest, MockCallRecords } from '../call-recordings';
import { MockPersons } from '../persons';
import { BaseMockRouterContext, FeatureMockRouter } from './types';

export interface CallIntelRecordsMockRouterContext extends BaseMockRouterContext {
  mockCallRecordsInstance: MockCallRecords;
  mockPersonsInstance: MockPersons;
}

export const CallIntelRecordsMockRouter: FeatureMockRouter<CallIntelRecordsMockRouterContext> = {
  addMockRouterEndpoints: (router, { mockCallRecordsInstance }) => {
    router.schemaEndpoint(CallIntelligenceService.ListCalls, async (req, _, next) => {
      const request: CallIntelTypes.ListCallsRequest = await req.clone().json();
      const filter = extractCallFilterFromRequest(request);

      // Do not mock the call list if personId is not empty
      // This is already handled by the real API in request interceptor
      if (filter.personId?.length === 1 && filter.personId[0]?.startsWith('demo')) {
        next();
        return null;
      }

      // Generate and return Mock data
      const callRecording = mockCallRecordsInstance.getFilteredCallRecordsResponse(request, filter);
      return MockAPIRouter.json(callRecording);
    });
  },
  getRequestInterceptors: ({ interceptorIdPrefix, mockPersonsInstance, vertical }) => [
    {
      id: `${interceptorIdPrefix}-person-call-recordings`,
      interceptor: async (request) => {
        const { pathname } = new URL(request.url);

        if (pathname !== '/call-intelligence/v2/calls') {
          return request;
        }

        const requestBody: CallIntelTypes.ListCallsIO['input'] = await request.clone().json();

        const { personId: personIdList } = extractCallFilterFromRequest(requestBody);
        const personId = personIdList?.[0] ?? '';

        // If personId is not provided, return the original request
        // This will be handled by the mock interceptor
        if (!personId || !personId.startsWith('demo')) {
          return request;
        }

        const personData = mockPersonsInstance.getFilteredPersons({
          personId,
        })?.[0];

        if (!personData) {
          return request;
        }

        if (requestBody.filter && personIdList) {
          requestBody.filter[CallIntelTypes.FilterTypeEnum.FILTER_BY_PERSON_ID].personId = personIdList.map((id) =>
            id.replace('demo-', '')
          );
        }

        return new Request(request, {
          body: JSON.stringify({
            ...requestBody,
            isCallTrendDemo: true,
            demoMetadata: {
              vertical,
              firstName: personData.firstName,
              lastName: personData.lastName,
              gender: personData.gender,
            },
          } as CallIntelTypes.ListCallsIO['input']),
        });
      },
    },
  ],
  invalidateQueries: (queryClient) => {
    queryClient.invalidateQueries([serviceName, 'ListCalls']);
  },
};
